/**
 * Resume-path resolution for hub (re)connects — the page a hub reopens must be
 * a page stored FOR THAT HUB.
 *
 * `lastHubPath:<key>` is written per connection identity by the router. Reading
 * it must use only the keys of the connection being resumed (`getHubOwnKeys`):
 * the reader used to walk every key `getHubSessionKeys()` returns — which also
 * yields `otto_active_hub_key`, i.e. the hub the client is *leaving* — and take
 * the first non-empty value. Connecting to hub B then resumed hub A's page, and
 * an id-bearing path (`users/<id>`, `integrations/<id>`) opened against the
 * wrong hub rendered "not found" / empty: the profile page looked unreachable
 * over the WebRTC connection while it worked on the hub the path came from.
 *
 * Cloud hub ids are not interchangeable: a hub user id exists on exactly one
 * hub, and the same hub restores its own page exactly as before.
 */
import { getHubOwnKeys } from '../config/runtime';

/** The slice of Storage this module needs — injectable so it is testable. */
export interface LastPathStorage {
  getItem(key: string): string | null;
}

function defaultStorage(): LastPathStorage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

/** The last position stored under `keys`, skipping empty and unreadable entries. */
export function readLastHubPath(keys: string[], storage?: LastPathStorage | null): string | null {
  const store = storage === undefined ? defaultStorage() : storage;
  if (!store) return null;
  for (const key of keys) {
    try {
      const value = store.getItem(`lastHubPath:${key}`);
      if (value) return value;
    } catch {
      // Unreadable entry — try the next key instead of failing the resume.
    }
  }
  return null;
}

/**
 * Cloud/WebRTC resume path: this hub's own last page, never another hub's.
 * Cameras are skipped (re-entering straight into a burst of snapshot requests
 * makes the connect feel broken) and a hub with no stored position lands on its
 * dashboards.
 */
export function cloudResumePath(hubId: string, storage?: LastPathStorage | null): string {
  const lastPath = readLastHubPath(getHubOwnKeys({ hubId }), storage);
  const normalized = lastPath?.replace(/^\/+/, '');
  if (normalized && !normalized.startsWith('cameras')) {
    return `/h/${hubId}/${normalized}`;
  }
  return `/h/${hubId}/layouts`;
}

/**
 * LAN / Electron-local resume path (without the leading slash) for the endpoint
 * the client is pointed at now, or null when this endpoint has no stored page.
 */
export function localResumePath(apiUrl: string | null | undefined, storage?: LastPathStorage | null): string | null {
  const lastPath = readLastHubPath(getHubOwnKeys({ apiUrl }), storage);
  if (!lastPath) return null;
  return lastPath.replace(/^\/+/, '');
}
